/**
 * useResendCountdown — the "wait N seconds before sending another code" countdown.
 *
 * Every surface that emails or texts a one-time code offers "Resend", and every server behind one
 * refuses a resend inside a cooldown (`@puffin-core/auth-routes`' password reset: 60 s,
 * `resendCooldownMs`; the MFA OTP send: the same window). A resend button that is live during that
 * window invites a refusal the user could not have predicted, so the countdown is shown instead.
 *
 * Promoted from `useOtpSendFlow`'s module-private copy (UNREACHABLE-MODULE-BURNDOWN-001 L16), when
 * `@puffin-core/login-shell`'s password-reset modal needed the same countdown — a second copy is
 * how the fleet's three `useRateLimit`s diverged (Q-UMB-8). One addition came with the promotion:
 * `start(seconds)` takes an OVERRIDE, because the server is the authority on its own cooldown — a
 * refusal carries `retryAfter`, and the countdown must resynchronise to it rather than restart at
 * the client's default.
 *
 * The cooldown is a DEADLINE, read by the ONE countdown (`useSecondsUntil`, POS-STATION-IDLE-CLOCK-001 prompt 2).
 * It used to subtract one from a number on every tick, so a tab the browser throttled, or a laptop that slept,
 * counted slow and held "Resend" off long after the server would have taken it.
 *
 * @module @puffin-core/mfa-ui/hooks/useResendCountdown
 */

import { useState, useCallback } from 'react';
// The LEAF, never page-shell's barrel: login-shell reaches this hook through the narrow `@puffin-core/mfa-ui/resend-countdown`
// and `/verification` subpaths, and its sign-in and reset path imports page-shell only by narrow subpaths. The barrel
// brought react-router onto the login and step-up path (POS-STATION-IDLE-CLOCK-001 prompt 2).
import { useSecondsUntil } from '@puffin-core/countdown';

const SECOND_MS = 1000;

/**
 * @param {number} cooldown - the default cooldown, in whole seconds
 * @returns {{
 *   secondsRemaining: number,
 *   start: (seconds?: number) => void,
 *   stop: () => void,
 * }} `start()` begins (or restarts) the countdown at `cooldown`, or at `seconds` when the server
 *   named its own; `stop()` zeroes it.
 */
export function useResendCountdown(cooldown) {
  const [deadlineMs, setDeadlineMs] = useState(null);
  const secondsRemaining = useSecondsUntil(deadlineMs) ?? 0;

  const start = useCallback(
    (seconds = cooldown) => {
      setDeadlineMs(Date.now() + seconds * SECOND_MS);
    },
    [cooldown],
  );

  const stop = useCallback(() => {
    setDeadlineMs(null);
  }, []);

  return { secondsRemaining, start, stop };
}

export default useResendCountdown;
